import { MSAData, SecondaryStructure } from './types';
import { DataLoader } from './core/data-loader';
import { validateMSAData } from './core/validators';
import { handleLabelClick } from './utils/rnacentral-utils';
import { loadNightingaleComponents } from './utils/nightingale-loader';
import { NavigationTrack } from './tracks/navigation/navigation-track';
import { LinksTrack } from './tracks/links/links-track';
import { SequenceTrack } from './tracks/sequence/sequence-track';
import { MSATrack } from './tracks/msa/msa-track';
import { LogoTrack } from './tracks/logo/logo-track';
import { MSAViewerGetters } from './getters';
import { MSAViewerSetters } from './setters';
import {
  ExportFormat,
  SequenceCountOption,
  exportAlignmentImage,
  getAvailableSequenceCountOptions,
} from './export/msa-image-exporter';

export class MSAViewer extends HTMLElement {
  // Public properties (accessible to getters/setters)
  public _data?: MSAData & { notation?: SecondaryStructure };
  public _apiEndpoint?: string;
  public _identifier?: string;
  public _apiUrl?: string;
  public _loading = false;
  public _error?: string;
  public _nightingaleLoaded = false;
  public _sequenceLength = 0;
  public _height = 400;
  public _width = 800;
  public _labelWidth = 200;
  public _displayStart = 1;
  public _displayEnd = 80;
  
  private _navigationTrack?: NavigationTrack;
  private _linksTrack?: LinksTrack;
  private _secondaryStructureTrack?: SequenceTrack;
  private _logoTrack?: LogoTrack;
  private _msaTrack?: MSATrack;
  private _scrollboxObserver?: IntersectionObserver;
  
  constructor() {
    super();
    this.className = 'msa-viewer';
    this.loadNightingale();
  }

  static get observedAttributes(): string[] {
    return ['api-endpoint', 'identifier', 'api-url', 'height', 'width', 'label-width'];
  }

  // Getters using external class
  get apiEndpoint(): string | undefined { return MSAViewerGetters.getApiEndpoint(this); }
  get identifier(): string | undefined { return MSAViewerGetters.getIdentifier(this); }
  get height(): number { return MSAViewerGetters.getHeight(this); }
  get width(): number { return MSAViewerGetters.getWidth(this); }
  get labelWidth(): number { return MSAViewerGetters.getLabelWidth(this); }

  // Setters using external class
  set apiEndpoint(value: string | undefined) { MSAViewerSetters.setApiEndpoint(this, value); }
  set identifier(value: string | undefined) { MSAViewerSetters.setIdentifier(this, value); }
  set height(value: number) { MSAViewerSetters.setHeight(this, value); }
  set width(value: number) { MSAViewerSetters.setWidth(this, value); }
  set labelWidth(value: number) { MSAViewerSetters.setLabelWidth(this, value); }

  // Zoom methods
  public zoomIn(): void {
    this._navigationTrack?.zoomIn();
  }

  public zoomOut(): void {
    this._navigationTrack?.zoomOut();
  }

  private async loadNightingale(): Promise<void> {
    try {
      await loadNightingaleComponents();
      this._nightingaleLoaded = true;
      if (this._data) {
        this.render();
      }
    } catch (error) {
      this._error = 'Failed to load MSA visualization library';
      this.render();
    }
  }

  // Public methods (needed by setters)
  public updateApiUrl(): void {
    if (this._apiEndpoint && this._identifier) {
      const endpoint = this._apiEndpoint.replace(/\/$/, '');
      this._apiUrl = `${endpoint}/${this._identifier}`;
      this.loadDataFromApi();
    }
  }

  public updateTrackConfig(config: Partial<any>): void {
    // For now, we'll re-render when config changes
    if (this._data) {
      this.render();
    }
  }

  async connectedCallback(): Promise<void> {
    const apiEndpointAttr = this.getAttribute('api-endpoint');
    const identifierAttr = this.getAttribute('identifier');
    
    if (apiEndpointAttr && identifierAttr) {
      this.apiEndpoint = apiEndpointAttr;
      this.identifier = identifierAttr;
    }
  }

  disconnectedCallback(): void {
    // Clean up intersection observer
    if (this._scrollboxObserver) {
      this._scrollboxObserver.disconnect();
      this._scrollboxObserver = undefined;
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue !== newValue) {
      switch (name) {
        case 'api-endpoint':
          this.apiEndpoint = newValue;
          break;
        case 'identifier':
          this.identifier = newValue;
          break;
        case 'height':
          this.height = parseInt(newValue) || 400;
          break;
        case 'width':
          this.width = parseInt(newValue) || 800;
          break;
        case 'label-width':
          this.labelWidth = parseInt(newValue) || 200;
          break;
      }
    }
  }

  private async loadDataFromApi(): Promise<void> {
    if (!this._apiUrl) return;
    
    this._loading = true;
    this._error = undefined;
    this.render();
    
    try {
      const data = await DataLoader.loadMSAData(this._apiUrl);
      if (validateMSAData(data)) {
        this._data = data;
        this._sequenceLength = Math.max(...data.sequences.map(seq => seq.sequence.length));
        
        // Always start at position 1 and set end based on sequence length
        this._displayStart = 1;
        this._displayEnd = this.getDefaultDisplayEnd(this._sequenceLength);
      } else {
        this._error = 'Invalid MSA data format';
      }
    } catch (error) {
      this._error = error instanceof Error ? error.message : 'Unknown error';
    } finally {
      this._loading = false;
      this.render();
    }
  }

  private getDefaultDisplayEnd(sequenceLength: number, defaultValue: number = 80): number {
    return Math.min(sequenceLength, defaultValue);
  }

  private render(): void {
    if (this._loading) {
      this.innerHTML = '<div class="msa-loading">Loading...</div>';
      return;
    }

    if (this._error) {
      this.innerHTML = `<div class="msa-error">${this._error}</div>`;
      return;
    }

    if (!this._nightingaleLoaded) {
      this.innerHTML = '<div class="msa-loading">Loading components...</div>';
      return;
    }

    if (!this._data) {
      this.innerHTML = '<div class="msa-error">No data</div>';
      return;
    }

    this.renderTracks();
  }

  private renderTracks(): void {
    if (!this._data) return;

    // Create track configuration
    const trackConfig = {
      height: this._height,
      width: this._width,
      labelWidth: this._labelWidth,
      sequenceLength: this._sequenceLength,
      displayStart: this._displayStart,
      displayEnd: this._displayEnd
    };

    const trackData = {
      msaData: this._data,
      notation: this._data.notation
    };

    // Create track instances
    this._navigationTrack = new NavigationTrack(trackConfig, trackData);
    this._msaTrack = new MSATrack(trackConfig, trackData);
    
    // Determine whether R-scape data is present and meaningful
    const rscapeHasData = !!this._data.notation?.rscape && this._data.notation.rscape.includes('2');

    // Create secondary structure track if secondary structure data exists
    if (this._data.notation) {
      this._linksTrack = new LinksTrack(trackConfig, trackData);
      this._secondaryStructureTrack = new SequenceTrack(
        {
          ...trackConfig,
          label: 'Secondary structure consensus',
          dataSource: 'notation',
          sequenceName: 'Notation',
          applyRscapeColor: rscapeHasData,
        },
        trackData
      );
    }

    // Logo track is always shown when MSA data is available
    this._logoTrack = new LogoTrack(trackConfig, trackData);

    // Generate HTML using track classes
    const navigationHTML = this._navigationTrack.createHTML();
    const linksHTML = this._linksTrack ? this._linksTrack.createHTML() : '';
    const secondaryStructureHTML = this._secondaryStructureTrack ? this._secondaryStructureTrack.createHTML() : '';
    const logoHTML = this._logoTrack.createHTML();
    const msaHTML = this._msaTrack.createHTML();

    // Create single MSA track with all sequences in scrollbox
    const allSequences = this._data.sequences;

    // Single MSA track with all sequences
    const msaWithScrollbox = `
      <nightingale-scrollbox 
        id="msa-scrollbox" 
        root-margin="50px" 
        disable-scroll-with-ctrl
        style="width: 100%; display: block;">
        <nightingale-scrollbox-item id="msa-item-main">
          ${msaHTML}
        </nightingale-scrollbox-item>
      </nightingale-scrollbox>
    `;

    // Set up the main container with configurable width
    this.innerHTML = `
      <nightingale-manager style="width: ${this._width}px;">
        ${this.renderExportToolbarHTML()}
        ${navigationHTML}
        ${linksHTML}
        ${secondaryStructureHTML}
        ${logoHTML}
        ${msaWithScrollbox}
      </nightingale-manager>
    `;

    // Setup tracks after DOM is ready
    setTimeout(() => {
      const container = this.querySelector('nightingale-manager') as HTMLElement;
      if (container) {
        this._navigationTrack?.setup(container);
        this._linksTrack?.setup(container);
        this._secondaryStructureTrack?.setup(container);
        this._logoTrack?.setup(container);

        this._msaTrack?.setup(container);
        
        // Set up MSA data for the single scrollbox item
        const scrollboxElement = container.querySelector('#msa-scrollbox');
        if (scrollboxElement) {
          const msaElement = container.querySelector('#msa-track') as HTMLElement;
          if (msaElement && allSequences.length > 0) {
            (msaElement as any).data = allSequences;
          }
        }
        
        // Base-pair highlighting on consensus-track hover
        this.setupBasePairHighlighting(container);

        // Setup event delegation for label clicks
        this.setupEventDelegation();

        this.setupExportToolbar(container);
      }
    }, 200);
  }

  private renderExportToolbarHTML(): string {
    if (!this._data) return '';

    const total = this._data.sequences.length;
    const countOptions = getAvailableSequenceCountOptions(total);
    const optionsHTML = countOptions
      .map((opt) => {
        const value = opt === 'all' ? 'all' : opt.toString();
        const label = opt === 'all' ? `All (${total})` : `First ${opt}`;
        return `<option value="${value}">${label}</option>`;
      })
      .join('');

    return `
      <div class="msa-export-toolbar" style="display: flex; justify-content: flex-end; align-items: center; gap: 8px; padding: 0 16px 8px 0; box-sizing: border-box;">
        <label style="font-size: 13px;">
          Sequences:
          <select id="msa-export-count">${optionsHTML}</select>
        </label>
        <label style="font-size: 13px;">
          Format:
          <select id="msa-export-format">
            <option value="png">PNG</option>
            <option value="svg">SVG</option>
          </select>
        </label>
        <button id="msa-export-btn" type="button">Download image</button>
      </div>
    `;
  }

  private setupExportToolbar(container: HTMLElement): void {
    const button = container.querySelector('#msa-export-btn') as HTMLButtonElement | null;
    if (!button) return;

    // renderTracks() can run more than once for the same data (e.g. redundant
    // attribute/property assignment during connectedCallback), which would
    // otherwise stack duplicate listeners on the same button.
    button.removeEventListener('click', this.handleExportClick);
    button.addEventListener('click', this.handleExportClick);
  }

  private handleExportClick = async (): Promise<void> => {
    const button = this.querySelector('#msa-export-btn') as HTMLButtonElement | null;
    const countSelect = this.querySelector('#msa-export-count') as HTMLSelectElement | null;
    const formatSelect = this.querySelector('#msa-export-format') as HTMLSelectElement | null;
    if (!button || !countSelect || !formatSelect || button.disabled) return;

    const rawCount = countSelect.value;
    const sequenceCount: SequenceCountOption = rawCount === 'all' ? 'all' : parseInt(rawCount, 10);
    const format = formatSelect.value as ExportFormat;

    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = 'Preparing…';
    try {
      await this.downloadImage({ sequenceCount, format });
    } catch (error) {
      console.error('Failed to export alignment image:', error);
    } finally {
      button.disabled = false;
      button.textContent = originalLabel;
    }
  };

  /**
   * Download a high-resolution SVG or PNG snapshot of the alignment
   * (ruler, secondary structure, sequence logo, and MSA grid) covering the
   * first `sequenceCount` sequences across every alignment column.
   */
  public async downloadImage(options: {
    sequenceCount?: SequenceCountOption;
    format?: ExportFormat;
    pngScale?: number;
  } = {}): Promise<void> {
    if (!this._data) return;

    await exportAlignmentImage(this._data, this._data.notation, this._sequenceLength, {
      sequenceCount: options.sequenceCount ?? 100,
      format: options.format ?? 'png',
      pngScale: options.pngScale,
      title: this._identifier,
    });
  }

  private setupEventDelegation(): void {
    this.removeEventListener('click', this.handleLabelClick);
    this.addEventListener('click', this.handleLabelClick);
  }

  private handleLabelClick = (event: Event): void => {
    handleLabelClick(event, this, (url: string) => {
      window.open(url, '_blank', 'noopener,noreferrer');
    });
  };

  /**
   * When the cursor hovers over a position in the consensus sequence track,
   * highlight that position AND its base-pair partner (if one exists) across
   * all tracks registered with nightingale-manager.
   */
  private setupBasePairHighlighting(container: HTMLElement): void {
    const consensusEl = container.querySelector('#consensus-sequence-track');
    const manager = this.querySelector('nightingale-manager') as HTMLElement | null;
    const basePairs = this._data?.notation?.basePairs;

    if (!consensusEl || !manager || !basePairs?.length) return;

    // Propagate a highlight string to every track in the manager
    const setHighlight = (highlight: string) => {
      ((manager as any).htmlElements as Set<HTMLElement> | undefined)?.forEach((el) => {
        if (highlight) {
          el.setAttribute('highlight', highlight);
        } else {
          el.removeAttribute('highlight');
        }
      });
    };

    consensusEl.addEventListener('change', (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        eventType?: string;
        feature?: { position?: number };
      } | undefined;

      if (detail?.eventType === 'mouseover' && detail.feature?.position != null) {
        // Stop the raw event so the manager doesn't partially override our highlight
        e.stopPropagation();

        const pos = detail.feature.position; // 1-indexed
        const pair = basePairs.find((bp) => bp.x === pos || bp.y === pos);
        if (pair) {
          const partner = pair.x === pos ? pair.y : pair.x;
          setHighlight(`${pos}:${pos},${partner}:${partner}`);
        } else {
          setHighlight(`${pos}:${pos}`);
        }
      } else if (detail?.eventType === 'mouseout') {
        e.stopPropagation();
        setHighlight('');
      }
    });
  }
}

if (!customElements.get('msa-viewer')) {
  customElements.define('msa-viewer', MSAViewer);
}